import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useSlideCopy } from '@/lib/i18n'
import type { SlideProps } from '@/components/SlideWizard'
import { copy } from './Slide8Practice.copy'
import robertChenAvatar from '@/assets/robert-chen-avatar.jpg'

const ease = [0.16, 1, 0.3, 1] as const

export default function Slide8Practice({ onComplete }: SlideProps) {
  const c = useSlideCopy(copy)
  const RED_FLAGS = [
    { id: 'secrecy', label: c.flagSecrecyLabel, annotation: c.flagSecrecyAnnotation },
    { id: 'too-good', label: c.flagTooGoodLabel, annotation: c.flagTooGoodAnnotation },
    { id: 'payment', label: c.flagPaymentLabel, annotation: c.flagPaymentAnnotation },
    { id: 'stranger', label: c.flagStrangerLabel, annotation: c.flagStrangerAnnotation },
    { id: 'access', label: c.flagAccessLabel, annotation: c.flagAccessAnnotation },
  ] as const

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [revealed, setRevealed] = useState(false)

  const toggleFlag = (id: string) => {
    if (revealed) return
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleCheck = () => {
    setRevealed(true)
    onComplete()
  }

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 mx-auto max-w-5xl">
      <div className="w-full grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-center lg:gap-10">
        {/* Left: Social media message */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.45, delay: 0.08, ease }}
          className="mx-auto w-full max-w-[320px] lg:max-w-[360px]"
        >
          <div className="relative">
            {/* Side button */}
            <div className="absolute -start-[3px] top-24 h-8 w-[2px] rounded-full bg-slate-600/45" />

            {/* Phone frame */}
            <div className="relative overflow-hidden rounded-t-[2.5rem] border-[4px] border-b-0 border-slate-900 bg-white">
              {/* Status bar */}
              <div className="bg-white px-6 pt-3 pb-1">
                <div className="relative flex items-center justify-between">
                  <span className="text-[13px] font-semibold text-slate-900">9:41</span>
                  <div className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 h-[22px] w-[90px] rounded-full bg-black" />
                  <div className="flex items-center gap-1">
                    <svg className="h-3.5 w-3.5 text-slate-900" viewBox="0 0 16 16" fill="currentColor">
                      <rect x="1" y="10" width="3" height="5" rx="0.5" />
                      <rect x="5" y="7" width="3" height="8" rx="0.5" />
                      <rect x="9" y="4" width="3" height="11" rx="0.5" />
                      <rect x="13" y="1" width="3" height="14" rx="0.5" opacity="0.3" />
                    </svg>
                    <svg className="h-3.5 w-3.5 text-slate-900" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M8 3C5.5 3 3.3 4 1.8 5.6a.75.75 0 001.1 1C4.2 5.3 6 4.5 8 4.5s3.8.8 5.1 2.1a.75.75 0 001.1-1C12.7 4 10.5 3 8 3z" opacity="0.3" />
                      <path d="M8 6.5c-1.7 0-3.2.7-4.3 1.8a.75.75 0 001.1 1C5.7 8.4 6.8 8 8 8s2.3.4 3.2 1.3a.75.75 0 001.1-1C11.2 7.2 9.7 6.5 8 6.5z" opacity="0.6" />
                      <path d="M8 10c-.9 0-1.8.4-2.4 1a.75.75 0 001.1 1c.3-.4.8-.5 1.3-.5s1 .2 1.3.5a.75.75 0 001.1-1C9.8 10.4 8.9 10 8 10z" />
                    </svg>
                    <svg className="h-4.5 w-[22px] text-slate-900" viewBox="0 0 25 12" fill="currentColor">
                      <rect x="0" y="1" width="21" height="10" rx="2" stroke="currentColor" strokeWidth="1" fill="none" />
                      <rect x="22" y="4" width="2" height="4" rx="0.5" opacity="0.4" />
                      <rect x="1.5" y="2.5" width="14" height="7" rx="1" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Messenger header */}
              <div className="flex items-center gap-2.5 px-4 py-2 border-b border-slate-100 relative">
                <svg className="h-5 w-5 text-blue-500 dir-flip" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                </svg>
                <div className="h-8 w-8 shrink-0 overflow-hidden rounded-full">
                  <img
                    src={robertChenAvatar}
                    alt=""
                    className="h-full w-full scale-115 object-cover object-center"
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-[15px] font-semibold text-slate-900 truncate">
                    {c.senderName}
                  </p>
                  <p className="text-[11px] text-slate-400">
                    {c.messageRequestLabel}
                  </p>
                </div>
                {/* Messenger icon */}
                <svg className="ms-auto h-6 w-6 text-blue-500 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.2 5.42 3.12 7.17.16.15.26.36.27.58l.05 1.81c.02.63.66 1.04 1.24.79l2.02-.89c.17-.08.36-.1.55-.06.9.25 1.87.38 2.75.38 5.64 0 10-4.13 10-9.7S17.64 2 12 2zm5.95 7.47l-2.91 4.61c-.46.74-1.44.93-2.13.42l-2.31-1.73a.6.6 0 00-.72 0l-3.12 2.37c-.42.32-.96-.2-.68-.65l2.91-4.61c.46-.74 1.44-.93 2.13-.42l2.31 1.73a.6.6 0 00.72 0l3.12-2.37c.42-.32.96.2.68.65z" />
                </svg>
              </div>

              {/* Message area */}
              <div className="px-4 py-4 bg-white min-h-[200px]">
                <p className="text-center text-[12px] text-slate-400 mb-3">
                  {c.prefaceLabel}
                </p>
                <div className="inline-block rounded-2xl rounded-ss-sm bg-slate-100 px-3.5 py-2.5">
                  <p className="text-[15px] text-slate-800 leading-relaxed">
                    {c.messageBody}
                  </p>
                </div>
              </div>

              {/* Accept/Delete bar */}
              <div className="flex items-center justify-center gap-4 px-4 py-3 border-t border-slate-100">
                <span className="text-[15px] font-semibold text-blue-500">{c.acceptLabel}</span>
                <span className="text-[15px] font-semibold text-slate-400">{c.deleteLabel}</span>
              </div>

            </div>
          </div>
        </motion.div>

        {/* Right: Quiz options */}
        <div className="mx-auto w-full max-w-sm lg:max-w-none lg:border-s lg:border-slate-200/80 lg:ps-10">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.2, ease }}
            className="text-xl sm:text-[1.4rem] font-semibold text-slate-800"
          >
            {c.question}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.35, ease }}
            className="mt-2 text-base text-slate-400"
          >
            {c.tapHint}
          </motion.p>

          <div className="mt-5 space-y-2.5">
            {RED_FLAGS.map((flag, i) => {
              const isSelected = selected.has(flag.id)

              return (
                <motion.button
                  key={flag.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.45 + i * 0.06, ease }}
                  onClick={() => toggleFlag(flag.id)}
                  disabled={revealed}
                  className={`w-full rounded-xl border px-4 py-3 text-start transition-all cursor-pointer ${
                    revealed
                      ? 'border-violet-300 bg-violet-50 text-slate-800'
                      : isSelected
                        ? 'border-violet-400 bg-violet-50 text-slate-900 font-medium'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors ${
                        revealed || isSelected
                          ? 'border-violet-400 bg-violet-500 text-white'
                          : 'border-slate-300 bg-white'
                      }`}
                    >
                      {(revealed || isSelected) && (
                        <svg
                          className="h-3 w-3"
                          viewBox="0 0 12 12"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                        >
                          <path d="M2.5 6.5L5 9l4.5-6" />
                        </svg>
                      )}
                    </span>
                    <span className="text-[15px] sm:text-base leading-snug">
                      {flag.label}
                    </span>
                  </span>
                  {revealed && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3, delay: 0.1 }}
                      className="mt-1 ps-8 text-sm text-violet-600"
                    >
                      {flag.annotation}
                    </motion.p>
                  )}
                </motion.button>
              )
            })}
          </div>

          <AnimatePresence mode="wait">
            {!revealed && selected.size >= 1 && (
              <motion.button
                key="check-btn"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, ease }}
                onClick={handleCheck}
                className="mt-5 w-full rounded-full border border-slate-200 px-8 py-3 text-base font-medium text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors cursor-pointer"
              >
                {c.checkLabel}
              </motion.button>
            )}

            {revealed && (
              <motion.p
                key="reveal-text"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3, ease }}
                className="mt-5 text-base sm:text-lg text-slate-500 leading-relaxed"
              >
                {c.revealPrefix}
                <span className="font-semibold text-slate-900">
                  {selected.size}{c.revealOf5}
                </span>
                {c.revealSuffix}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}
